/**
 * Client side Benzinga implementation of https://github.com/tradingview/charting_library/wiki/JS-Api
 */

import * as protos from './lastdb';
import { Bar, parseBars, Resolutions } from './chartUtils';
import { AuthenticationManager, LoggingManager, Session, StockSymbol } from '@benzinga/session';
import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ChartEnvironment } from '../environment';

const pb: any = protos.quotestore;

interface IRequest {
  onResponse: ResponseCallback;
  request: protos.quotestore.IRequest;
}

declare type ResponseCallback = (response: protos.quotestore.Response) => void;

export interface IConfig {
  addr: string;
  onReconnect?: () => void;
}

export class BarSocket {
  private socket!: WebSocket;
  private config: IConfig;
  private socketRequestId = 1;
  private readyCallback?: () => void;

  private requests: Map<number, IRequest> = new Map();

  private autoReconnect = true;

  private retryInterval = 5000;

  private ready = false;
  private isFirstConnect = true;
  private fistCandleTime = new Map<
    string,
    {
      time: number;
      noData: boolean;
    }
  >();

  private tickerToFullName = new Map<string, string>();

  private session;

  constructor(session: Session) {
    this.config = {
      addr: session.getEnvironment(ChartEnvironment).socketUrl.toString(),
    };
    this.session = session;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  log(msg: string, ...args: any[]) {
    if (args.length > 0) {
      this.session.getManager(LoggingManager).log('debug', { category: 'ChartAPI', message: `${msg} ${args}` });
    } else {
      this.session.getManager(LoggingManager).log('debug', { category: 'ChartAPI', message: `${msg}` });
    }
  }

  async getTickerFullName(symbolName: string): SafePromise<string> {
    this.log(`resolveSymbol(symbolName=${symbolName})`);

    const resolveSymbolRequest = pb.ResolveSymbolRequest.create({ symbolName });
    const request = protos.quotestore.Request.create({ resolveSymbolRequest });
    return new Promise(resolve => {
      this.doRequest(request, (response: protos.quotestore.Response) => {
        if (response.error) {
          resolve({ err: new SafeError(response.error.message as string, response.error.message as string) });
          return;
        }
        const symbol = response.resolveSymbolResponse;
        if (!symbol) {
          resolve({
            err: new SafeError(
              `Response is null: symbolName=${symbolName}`,
              `Response is null: symbolName=${symbolName}`,
            ),
          });
          return;
        }

        const fullName = `${symbol.exchange}:${symbol.name}`;
        this.tickerToFullName.set(symbolName, fullName);
        resolve({ ok: fullName });
      });
    });
  }

  async getBars(
    ticker: StockSymbol,
    resolution: Resolutions,
    from: number,
    /**
     * Unix timestamp (rightmost requested bar - not inclusive)
     */
    to: number,
    /**
     * The exact amount of bars to load, should be considered a higher priority than `from` if your datafeed supports it
     */
    countBack?: number,
    /**
     * Used to identify if it's the first call of getBars
     */
    firstDataRequest?: boolean,
  ): SafePromise<{ candles: Bar[]; nextTime?: number; noData?: boolean }> {
    if (!this.tickerToFullName.has(ticker)) {
      const fullName = await this.getTickerFullName(ticker);
      if (fullName.err) {
        return { err: fullName.err };
      }
    }
    const fullName = this.tickerToFullName.get(ticker);
    const name = `${fullName}_${resolution}`;
    const fromDate = new Date(from * 1000);
    const toDate = new Date(to * 1000);

    // this is a hack since out backend simply turns on noData when there is no previous data but TC wants there to be
    // no candles in the response so this hack simply makes it so we don't have to send one more request to the backend
    // simply to tell us it does not have more data when we already know that.
    const item = this.fistCandleTime.get(name);
    if (item?.noData && to <= item?.time) {
      return {
        ok: {
          candles: [],
          noData: true,
        },
      };
    }
    this.log(
      `getBars(symbol=${ticker}, countBack=${countBack}, firstDataRequest=${firstDataRequest}, from=${fromDate}, to=${toDate}, resolution=${resolution})`,
    );
    const symbol = fullName;
    const getBarsRequest = pb.GetBarsRequest.create({
      countBack,
      from,
      resolution,
      symbol,
      // backend includes always includes to so we don't want to do that on next request
      to: firstDataRequest ? to : to - 1,
    });
    const request = pb.Request.create({
      getBarsRequest,
    });

    return new Promise(resolve => {
      this.doRequest(request, (response: any) => {
        if (response.error) {
          resolve({
            ok: {
              candles: [],
              noData: true,
            },
          });
          return;
        }

        if ((response.getBarsResponse as any)?.noData && (response.getBarsResponse?.bars?.length ?? 0) > 0) {
          const item = this.fistCandleTime.get(name);
          if (!item?.noData) {
            this.fistCandleTime.set(`${fullName}_${resolution}`, {
              noData: response.getBarsResponse.noData,
              time: response.getBarsResponse.bars?.[0].time ?? 0,
            });
          }
        }
        if (response.getBarsResponse?.bars) {
          resolve({
            ok: {
              candles: parseBars(response.getBarsResponse.bars),
              noData: !!response.getBarsResponse.noData && response.getBarsResponse.bars.length === 0,
            },
          });
        } else {
          /*
           * We should be able to call onError, but the TV library puts the chart
           * into an unusable state if that happens, instead return no data.
           */
          this.log(
            `getBars() failed: ${(response.error as unknown as protos.quotestore.IError)?.message ?? 'Unknown error'}`,
          );
          resolve({ err: new SafeError('No data', 'No data') });
        }
      });
    });
  }

  public connect(): SafePromise<boolean> {
    const endpoint = this.config.addr;

    this.socket = new WebSocket(endpoint);
    this.socket.binaryType = 'arraybuffer';

    return new Promise(resolve => {
      // Listen for messages
      this.socket.addEventListener('message', event => {
        const msg = protos.quotestore.Response.decode(new Uint8Array(event.data));
        if (msg.type !== 'heartbeat' && msg.type !== 'candleTick') {
          this.log(`< ${msg.type}: `, msg);
        }
        /*
         * If the response has a requestId (was an rpc), call the callback, otherwise
         * handle depending on type
         */
        if (this.requests.has(msg.requestId)) {
          const r = this.requests.get(msg.requestId);
          r?.onResponse(msg);
        }
      });

      this.socket.addEventListener('open', (event: Event) => {
        this.log(`Connected to ${endpoint}`, event);
        resolve(this.authenticate());
      });

      this.socket.onerror = (event: Event) => {
        this.log('error', event);
        resolve({ err: new SafeError('Failed to connect', 'Failed to connect') });
      };

      this.socket.onclose = (event: CloseEvent) => {
        this.log(`Socket closed to ${endpoint}`, event);
        this.ready = false;
        if (this.autoReconnect) {
          this.log(`Scheduled to reconnect in ${this.retryInterval}ms.`);
          setTimeout(() => {
            this.connect();
          }, this.retryInterval);
        }
      };
    });
  }

  close() {
    this.autoReconnect = false;

    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
  }

  /**
   * Methods below are not part of the js-api and should be moved to a separate component
   */

  private doRequest(request: protos.quotestore.IRequest, onResponse: ResponseCallback) {
    if (!request.authRequest && !this.ready) {
      const response = new protos.quotestore.Response({
        error: { message: 'Not ready' },
      });
      onResponse(response);
      return;
    }
    request.id = this.socketRequestId++;

    this.requests.set(request.id, { onResponse, request });
    this.log('doRequest request> ', request);
    const encodedRequest = protos.quotestore.Request.encode(request).finish();
    this.socket.send(encodedRequest);
  }

  private async authenticate(): SafePromise<boolean> {
    const request = protos.quotestore.Request.create({
      authRequest: { iamSessionId: this.session.getManager(AuthenticationManager).getBenzingaToken() },
    });
    return new Promise(resolve => {
      this.doRequest(request, (response: protos.quotestore.Response) => {
        if (response.error) {
          resolve({ err: new SafeError(response.error.message as string, response.error.message as string) });
        } else {
          // After auth call the
          this.ready = true;
          if (this.readyCallback) {
            const cb = this.readyCallback;
            this.log(`Authenticated, calling readyCallback`);

            setTimeout(() => cb.call(this));
            this.readyCallback = undefined;
          } else {
            this.log(`Authenticated, readyCallback not set.`);
          }

          if (!this.isFirstConnect && this.config.onReconnect) {
            this.config.onReconnect();
          }

          this.isFirstConnect = false;
          resolve({ ok: true });
        }
      });
    });
  }
}
